<template>
  <div class="irp-record-details" data-testid="irp-record-details">
    <BittsEmptyState
      v-if="!cells.length && !collapsedFields.length"
      class="irp-record-details__full-width"
      :font-awesome-icon="emptyIcon"
      :title="emptyMessage"
      :description="emptyDescription"
      data-testid="irp-record-details-empty"
    />
    <template v-else>
      <div
        v-for="cell in cells"
        :key="cell.key"
        class="irp-record-details__cell"
        :data-testid="`irp-record-details-${cell.key}`"
      >
        <span class="irp-record-details__label">{{ cell.label }}</span>
        <div class="irp-record-details__value">
          <FontAwesomeIcon
            v-if="cell.hasPersonIcon"
            :icon="['fak', 'person']"
            class="irp-record-details__icon"
          />
          <IRPFieldGridText
            :class="{ 'irp-record-details__amount': cell.isAmount }"
            :text="cell.value"
            :is-masked="true"
          />
        </div>
      </div>

      <div
        v-if="isOwnAccount"
        class="irp-record-details__cell"
        data-testid="irp-record-details-data-source"
      >
        <span class="irp-record-details__label">Data Source</span>
        <div class="irp-record-details__value">
          <BittsSvg
            v-if="dataSource"
            :svg="`${dataSource.integrationType}Icon`"
            class="irp-record-details__source-icon"
            :alt="DataSourceFriendlyName[dataSource.integrationType] ?? ''"
          />
          <IRPFieldGridText :text="dataSource?.label ?? EMPTY_FIELD_VALUE" />
        </div>
      </div>

      <div v-if="collapsedFields.length" class="irp-record-details__full-width">
        <IRPAllFieldsCollapse :fields="collapsedFields" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsSvg } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { computed } from 'vue';

import { DataSourceFriendlyName } from '@/constants/data_sources';
import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import {
  useFeedsStore,
  useIndividualRecordStore,
  useSourcesStore,
} from '@/stores';
import type {
  RecordCollectionType,
  RecordFieldType,
} from '@/types/individual_record';

import {
  EMPTY_FIELD_VALUE,
  HEADER_ARR_FIELD_NAMES,
  formatFieldNumber,
  formatFieldValue,
  hasFieldValue,
  openOpportunityCount,
} from './irp';
import IRPAllFieldsCollapse from './IRPAllFieldsCollapse.vue';
import IRPFieldGridText from './IRPFieldGridText.vue';

type CellType = {
  key: string;
  label: string;
  value: string;
  hasPersonIcon?: boolean;
  isAmount?: boolean;
};

const {
  fields,
  accountOwners,
  isOwnData = false,
  emptyMessage = 'No details to show',
} = defineProps<{
  fields: RecordFieldType[];
  accountOwners?: RecordCollectionType;
  isOwnData?: boolean;
  emptyMessage?: string;
}>();

const ACCOUNT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.ACCOUNT];
const LEAD_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.LEAD];
const USER_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.USER];

const UNAVAILABLE_COUNT = 'Unavailable';

const EMPTY_DESCRIPTIONS = {
  account: 'Account details on this record will appear here.',
  lead: 'Lead details on this record will appear here.',
} as const;

const EMPTY_ICONS: Record<'account' | 'lead', BittsIcon> = {
  account: ['fak', 'company'],
  lead: ['fak', 'person'],
};

const OWNER_LABELS = {
  account: 'Account Owner',
  lead: 'Lead Owner',
} as const;

const individualRecordStore = useIndividualRecordStore();
const sourcesStore = useSourcesStore();
const feedsStore = useFeedsStore();

const mdmType = computed(() => individualRecordStore.mdmType ?? 'account');

const isOwnAccount = computed(() => isOwnData && mdmType.value === 'account');

const emptyIcon = computed<BittsIcon>(() => EMPTY_ICONS[mdmType.value]);
const emptyDescription = computed(() => EMPTY_DESCRIPTIONS[mdmType.value]);

const ownerFields = computed(() =>
  (accountOwners?.items ?? []).flatMap((owner) => owner.fields),
);

const ownerNameFields = computed(() =>
  ownerFields.value.filter(
    (field) =>
      field.mdm_property === USER_PROPERTIES.NAME && hasFieldValue(field),
  ),
);

function fieldByMdmProperty(mdmProperty: string) {
  return fields.find((field) => field.mdm_property === mdmProperty);
}

function fieldByNames(names: string[]) {
  return fields.find(
    (field) =>
      names.includes(field.name?.toLowerCase()) ||
      names.includes(field.column?.toLowerCase()),
  );
}

const ownerCell = computed<CellType | null>(() => {
  if (!ownerNameFields.value.length) return null;
  const [firstOwner] = ownerNameFields.value;
  return {
    key: 'owner',
    label: firstOwner?.name || OWNER_LABELS[mdmType.value],
    value: ownerNameFields.value.map(formatFieldValue).join(', '),
    hasPersonIcon: true,
  };
});

const arrField = computed(() =>
  mdmType.value === 'lead' ? undefined : fieldByNames(HEADER_ARR_FIELD_NAMES),
);

const revenueCell = computed<CellType | null>(() => {
  const revenue = arrField.value;
  if (!revenue) return null;
  return {
    key: 'revenue',
    label: revenue.name || 'ARR',
    value:
      typeof revenue.value === 'number'
        ? formatFieldNumber(revenue.value)
        : formatFieldValue(revenue),
    isAmount: true,
  };
});

const partnerAccountCells = computed<(CellType | null)[]>(() => {
  const tier = fieldByMdmProperty(ACCOUNT_PROPERTIES.TYPE);
  return [
    ownerCell.value,
    revenueCell.value,
    tier
      ? {
          key: 'tier',
          label: tier.name || 'Tier',
          value: formatFieldValue(tier),
        }
      : null,
  ];
});

const nameField = computed(() => fieldByMdmProperty(ACCOUNT_PROPERTIES.NAME));

const ownAccountCells = computed<(CellType | null)[]>(() => [
  nameField.value
    ? {
        key: 'name',
        label: nameField.value.name || 'Name',
        value: formatFieldValue(nameField.value),
      }
    : null,
  revenueCell.value,
  ownerCell.value,
]);

const ownContactsCount = computed(() => {
  const { ownContactsSection: contacts } = individualRecordStore;
  if (contacts.hasLoaded)
    return String(contacts.own?.contacts.items.length ?? 0);
  return contacts.error ? UNAVAILABLE_COUNT : EMPTY_FIELD_VALUE;
});

const ownOpportunitiesCount = computed(() => {
  const { ownOpportunitiesSection: opportunities } = individualRecordStore;
  if (opportunities.hasLoaded)
    return String(
      openOpportunityCount(opportunities.own?.opportunities.items ?? []),
    );
  return opportunities.error ? UNAVAILABLE_COUNT : EMPTY_FIELD_VALUE;
});

const countCells = computed<CellType[]>(() => [
  { key: 'contacts', label: 'Contacts', value: ownContactsCount.value },
  {
    key: 'opportunities',
    label: 'Open Opportunities',
    value: ownOpportunitiesCount.value,
  },
]);

const dataSource = computed(() => {
  const source = sourcesStore.getSourceById(
    Number(individualRecordStore.sourceId),
  );
  const feed = source?.feed_id
    ? feedsStore.getFeedById(source.feed_id)
    : undefined;
  const integrationType = feed?.integration.type;
  if (!integrationType) return null;
  return {
    integrationType,
    label: DataSourceFriendlyName[integrationType] || integrationType,
  };
});

const leadCells = computed<(CellType | null)[]>(() => {
  const title = fieldByMdmProperty(LEAD_PROPERTIES.TITLE);
  const email = fieldByMdmProperty(LEAD_PROPERTIES.EMAIL);
  return [
    ownerCell.value,
    title
      ? {
          key: 'title',
          label: title.name || 'Title',
          value: formatFieldValue(title),
        }
      : null,
    email
      ? {
          key: 'email',
          label: email.name || 'Email',
          value: formatFieldValue(email),
        }
      : null,
  ];
});

const fieldCells = computed(() => {
  if (mdmType.value === 'lead') return leadCells.value;
  return isOwnAccount.value ? ownAccountCells.value : partnerAccountCells.value;
});

const cells = computed<CellType[]>(() => [
  ...fieldCells.value
    .filter((cell) => cell !== null)
    .filter((cell) => cell.value !== EMPTY_FIELD_VALUE),
  ...(isOwnAccount.value ? countCells.value : []),
]);

const listedProperties = computed<string[]>(() => {
  if (mdmType.value === 'lead')
    return [LEAD_PROPERTIES.TITLE, LEAD_PROPERTIES.EMAIL];
  return isOwnAccount.value
    ? [ACCOUNT_PROPERTIES.NAME]
    : [ACCOUNT_PROPERTIES.TYPE];
});

const listedFields = computed(() => {
  const properties = listedProperties.value;
  const listed = fields.filter(
    (field) => field.mdm_property && properties.includes(field.mdm_property),
  );
  return arrField.value ? [...listed, arrField.value] : listed;
});

const collapsedFields = computed(() =>
  [...fields, ...ownerFields.value].filter(
    (field) =>
      hasFieldValue(field) &&
      !listedFields.value.includes(field) &&
      !ownerNameFields.value.includes(field),
  ),
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-record-details {
  @apply grid items-center gap-x-24 gap-y-16;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.irp-record-details__full-width {
  grid-column: 1 / -1;
}

.irp-record-details__cell {
  @apply flex min-w-0 flex-col gap-4 text-base text-neutral-text;
}

.irp-record-details__label {
  @apply truncate text-sm font-semibold text-neutral-text-weak;
}

.irp-record-details__value {
  @apply flex min-w-0 items-center gap-8;
}

.irp-record-details__icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-record-details__source-icon {
  @apply h-16 w-16 shrink-0;
}

.irp-record-details__amount {
  @apply font-bold text-neutral-text-strong;
}
</style>
